import React, { useState, useEffect, useCallback } from 'react';

import { defaultHandler, getCompatibleHandler, faceOcclusionDefaultValues } from '@vlabs/pages/handlers/handlerConfig';
import { CreatedHandlerForm } from '@vlabs/pages/handlers/preconfigured-scenarios/index';
import {
  StepCommon,
  StepFaceEstimations,
  StepBodyEstimations,
  StepFilters,
  StepMatchPolicy,
  StepStoragePolicies,
  StepTagPolicy,
  StepFaceQuality,
  StepDeepfake,
  StepLiveness,
  StepPeopleCount,
  StepCallbacksPolicy,
  StepFaceOcclusion,
} from '@vlabs/pages/handlers/preconfigured-scenarios/other/steps';
import { useSteppedForm, useFaceOcclusionIsAvailable } from '@vlabs/shared/hooks';
import { selectAppServices } from '@vlabs/shared/selectors/appSelectors';
import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';
import { Stepper, Modal } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { selectHandlerOptions } from '@vlabs/pages/handlers/selectors';
import { createHandler } from '@vlabs/pages/handlers/store';

import st from './OtherScenarioStepper.module.sass';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';

const steps = [
  StepCommon,
  StepFaceEstimations,
  StepLiveness,
  StepDeepfake,
  StepFaceQuality,
  // StepFaceOcclusion, // пока добавляем отдельно только для версии lp 5.84.0
  StepBodyEstimations,
  StepPeopleCount,
  StepFilters,
  StepMatchPolicy,
  StepStoragePolicies,
  StepTagPolicy,
  StepCallbacksPolicy,
];

function OtherScenarioStepper({
  onSubmit,
  handlerOptions,
  listOptions,
  lpVersion,
}) {
  const { t } = useTranslation();
  const history = useHistory();

  const [createdModalIsOpen, setCreatedModalIsOpen] = useState(false);
  const [createdHandlerId, setCreatedHandlerId] = useState();
  const [createdHandlerName, setCreatedHandlerName] = useState();
  const [defaultValues, setDefaultValues] = useState(defaultHandler);

  const form = useForm({
    defaultValues,
    mode: 'onChange',
  });
  const { reset, handleSubmit, getValues } = form;

  // FIXME временное решение, в будущем добавлять StepFaceOcclusion по умолчанию
  const isFaceOcclusionAvailable = useFaceOcclusionIsAvailable();

  useEffect(() => {
    if (!lpVersion) return;
    const handler = getCompatibleHandler(lpVersion);
    setDefaultValues(handler);
    reset(handler);
  }, [lpVersion]);

  useEffect(() => {
    if (!isFaceOcclusionAvailable || steps.includes(StepFaceOcclusion)) return;
    steps.splice(5, 0, StepFaceOcclusion); // шаг 6 после StepFaceQualty

    const updated = {
      ...getValues(),
      ...faceOcclusionDefaultValues,
    };

    setDefaultValues(updated);
    reset(updated);
  }, [isFaceOcclusionAvailable]);

  const onSaveHandler = useCallback(async (values) => {
    setCreatedHandlerId(await onSubmit(values));
    setCreatedHandlerName(values?.description);
    setCreatedModalIsOpen(true);
  }, [onSubmit, setCreatedModalIsOpen]);

  const canGoNextStep = useCallback(() => !(document.querySelector('.ReactModal__Content--after-open')), []);
  const stepper = useSteppedForm({
    form,
    canGoNextStep,
    steps,
    onSubmit: onSaveHandler,
    buttonsClass: st.StepperButtons,
  });

  const onCloseCreatedModal = () => {
    setCreatedModalIsOpen(false);
    history.goBack();
  };

  const context = {
    ...form,
    listOptions,
    handlerOptions,
  };

  return (
    <Page>
      <PageContent>
      <PageHeader title={t('handlers:title.create')} />
      <FormProvider {...context}>
        <form onSubmit={handleSubmit(onSaveHandler)}>
          <Modal
            appElement={document.getElementById('root')}
            data-testid="modal.saveOtherHandler"
            isOpen={createdModalIsOpen}
            onRequestClose={onCloseCreatedModal}
            size="small"
          >
            <CreatedHandlerForm
              handlerId={createdHandlerId}
              handlerName={createdHandlerName}
            />
          </Modal>
          <Stepper {...stepper} className={st.StepperWrapper}>
            {steps.map((Step) => <Step key={Step.name} />)}
          </Stepper>
        </form>
      </FormProvider>
      </PageContent>
    </Page>
  );
}

OtherScenarioStepper.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  handlerOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  listOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  values: PropTypes.objectOf(PropTypes.any),
  lpVersion: PropTypes.shape({
    major: PropTypes.number,
    minor: PropTypes.number,
    patch: PropTypes.number,
  }),
};

OtherScenarioStepper.defaultProps = {
  handlerOptions: [],
  listOptions: [],
  values: undefined,
  lpVersion: undefined,
};

export default connect((state) => ({
  handlerOptions: selectHandlerOptions(state),
  listOptions: selectListOptions(state),
  lpVersion: selectAppServices(state)?.lunaPlatform?.versionNum,
}),
(dispatch) => ({
  onSubmit: (data) => dispatch(createHandler(data)),
}))(OtherScenarioStepper);
